import { CrudFilters, DataProvider } from '@refinedev/core';
import { stringify } from '@refinedev/simple-rest';
import { AxiosInstance } from 'axios';

import { getSelectedCountryId } from './contexts/CountryContext';

export const escapeRegexSpecialChars = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Keys que Refine inyecta en `meta` sin que las pase el llamador, y que no son
 * parametros del backend:
 * - `id` / `resource` / `action` / `to`: `useMeta` mergea los params de la ruta y del
 *   query string dentro del meta de todo hook de datos, asi que en `/tickets/show/123`
 *   cualquier useList de esa pantalla terminaba mandando `id=123` al backend.
 * - `queryContext` / `queryKey` / `signal`: metadata interna de Refine/react-query.
 */
const INTERNAL_META_KEYS = new Set([
  'id',
  'resource',
  'action',
  'to',
  'queryContext',
  'queryKey',
  'signal',
]);

export const sanitizeQueryParam = (key: string, value: any) => {
  // Prevent unescaped regex metacharacters from breaking backend searches
  if (key === 'search' && typeof value === 'string') {
    return escapeRegexSpecialChars(value);
  }

  return value;
};

/**
 * Arma los pares key=value de la query a partir del meta, descartando las keys internas
 * y los valores vacios. Los filtros reales (CrudFilters) no pasan por aca.
 */
export const buildMetaQuery = (meta: Record<string, any>) => {
  const metaQuery: Record<string, any> = {};

  Object.entries(meta).forEach(([key, value]) => {
    if (value !== undefined && value !== null && !INTERNAL_META_KEYS.has(key)) {
      metaQuery[key] = sanitizeQueryParam(key, value);
    }
  });

  return metaQuery;
};

export const dataProvider = (apiUrl: string, axiosInstance: AxiosInstance) => {
  const generateFilters = (filters: CrudFilters = []) => {
    const queryFilters: Record<string, any> = {};

    filters?.forEach(filter => {
      if ('field' in filter && filter.operator && filter.value !== undefined) {
        const { field, value } = filter;
        const sanitizedValue = sanitizeQueryParam(`${field}`, value);

        queryFilters[`${field}`] = sanitizedValue;
      }
    });

    /**
     * Parametro generalizado para funcionalidades de app operaciones
     */
    queryFilters.only_staff = true;

    const selectedCountryId = getSelectedCountryId();

    if (selectedCountryId != undefined) {
      queryFilters.only_br = selectedCountryId;
    }

    return queryFilters;
  };

  const provider: DataProvider = {
    create: async ({ resource, variables }) => {
      const url = `${apiUrl}/${resource}`;
      const { data } = await axiosInstance.post(url, variables);

      return {
        data,
      };
    },
    deleteOne: async ({ resource, id, variables }) => {
      const url = `${apiUrl}/${resource}/${id}`;
      const { data } = await axiosInstance.delete(url, {
        data: variables,
      });

      return {
        data,
      };
    },
    getList: async params => {
      const { resource, pagination, sorters, filters, meta } = params;
      const url = `${apiUrl}/${resource}`;
      const { currentPage = 1, pageSize = 20 } = pagination ?? {};
      const query: Record<string, any> = {
        page: currentPage,
        page_size: pageSize,
      };

      if (sorters && sorters.length > 0) {
        const sortersString = sorters.map(sorter => {
          return `${sorter.order === 'asc' ? '' : '-'}${sorter.field}`;
        });

        query.order_by = sortersString.join(',');
      }

      const queryFilters = generateFilters(filters);

      if (meta) {
        Object.assign(query, buildMetaQuery(meta));
      }

      const { data } = await axiosInstance.get(
        `${url}?${stringify({ ...query, ...queryFilters })}`,
      );

      if (Array.isArray(data)) {
        return {
          data: data,
          total: data.length,
        };
      } else {
        const { count, results } = data ?? {};

        return {
          data: results,
          total: count,
        };
      }
    },
    getOne: async params => {
      const { resource, meta, id } = params;
      const url = `${apiUrl}/${resource}/${id}`;
      const query: Record<string, any> = {};

      if (meta) {
        Object.assign(query, buildMetaQuery(meta));
      }

      query.only_staff = true;

      const selectedCountryId = getSelectedCountryId();

      if (selectedCountryId != undefined) {
        query.only_br = selectedCountryId;
      }

      const { data } = await axiosInstance.get(`${url}?${stringify({ ...query })}`);

      return {
        data,
      };
    },
    update: async ({ resource, id, variables }) => {
      const url = `${apiUrl}/${resource}/${id}`;
      const updatedData = Object.fromEntries(
        Object.entries(variables as Record<string, unknown>).map(([key, value]) => [
          key,
          value === undefined ? null : value,
        ]),
      );
      const { data } = await axiosInstance.patch(url, updatedData);

      return {
        data,
      };
    },
    getApiUrl: () => apiUrl,
  };

  return provider;
};
